import { v4 as uuid } from 'uuid';

import {
  setSubscribeUsersStatusFailed,
  setSubscribeUsersStatusPending,
  setSubscribeUsersStatusSuccess,
} from './actions';
import { setSocketStatusFailed } from '../../slices/socketStatusSlice';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { TThunkAction } from '../../types/redux/thunk';
import { TSessionSucceedState } from '../../types/session';
import { ETelemetryEvents } from '../../types/telemetry';
import { IWSClientSubscribeUsersStatusSchema } from '../../types/usersStatus';
import { timeoutPromise } from '../../utils/timeoutPromise';
import { EInternetStatus } from '../internetStatus';

/**
 * Подписка по сокету на получение статуса по массиву id пользователей
 */
export const subscribeUsersStatus =
  (requestedUserIds: number[]): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { logger, telemetry, custom: { socket } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents, eventChannel } = socket;

    const {
      session: { status, token },
      socketStatus,
      internetStatus,
    } = getState();

    dispatch(setSubscribeUsersStatusPending());

    // в модалке каждый раз когда возвращемся на экран - перезапрашивается сессия
    const isSessionExpired = status !== ERequestApiStatus.Succeed && status !== ERequestApiStatus.Pending;
    const isSocketIsClose = eventChannel.isChannelClosed() || socketStatus === ERequestApiStatus.Failed;
    const isOffline = internetStatus === EInternetStatus.Offline;

    const uid = uuid();

    if (isSocketIsClose) {
      dispatch(setSocketStatusFailed());
    }

    if (isSessionExpired || isSocketIsClose || isOffline) {
      clientEvents.set(uid, { type: EWsClientEventType.SubscribeUsersStatus, status: ERequestApiStatus.Failed });
      dispatch(setSubscribeUsersStatusFailed());

      telemetry.increment(ETelemetryEvents.ChatWsUserStatusFail);
      logger.warning('Не удалось подписаться на статус пользователей по ws', {
        domain: 'frontend-chats.subscribeUsersStatus.ts',
        message: 'Сессия истекла или соединение закрыто',
      });

      return;
    }

    const wsStatusEvent: IWSClientSubscribeUsersStatusSchema = {
      type: 'status',
      uid,
      userId: (token as TSessionSucceedState['token']).userId,
      sessionToken: (token as TSessionSucceedState['token']).sessionToken,
      requestedUserIds,
      action: 'subscribe',
    };

    clientEvents.set(uid, {
      status: ERequestApiStatus.Pending,
      type: EWsClientEventType.SubscribeUsersStatus,
    });

    try {
      await timeoutPromise(eventChannel.subscribeUsersStatus(wsStatusEvent), 10000);
      dispatch(setSubscribeUsersStatusSuccess());
    } catch (err) {
      logger.warning('Не удалось подписаться на статус пользователей по ws', {
        domain: 'frontend-chats.subscribeUsersStatus.ts',
        ...err,
      });
      telemetry.increment(ETelemetryEvents.ChatWsUserStatusFail);

      clientEvents.set(uid, { type: EWsClientEventType.SubscribeUsersStatus, status: ERequestApiStatus.Failed });
      dispatch(setSubscribeUsersStatusFailed());
    }
  };
